/**
 * Vue d'ensemble du dashboard client — reproduction 1:1 de
 * src/components/dashboard/DashboardOverview.tsx + ses sous-composants
 * (WelcomeCard, RequestSummary, RecentDocuments, RecentInvoices, HelpCard).
 *
 * Sous-composants regroupés ici pour limiter la fragmentation.
 */
import { HelpCircle, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { getStatusColor } from './status-color';

export type DashboardRequest = {
  id: string;
  address: string;
  status: string;
  date: string;
  progress: number;
};
export type DashboardDocument = {
  id: string;
  name: string;
  status: string;
  date: string;
};
export type DashboardInvoice = {
  id: string;
  amount: string;
  status: string;
  date: string;
};

export function DashboardOverview({
  userData,
  requests,
  documents,
  invoices,
}: {
  userData: { name: string; plan: string; address: string };
  requests: DashboardRequest[];
  documents: DashboardDocument[];
  invoices: DashboardInvoice[];
}) {
  return (
    <div className="space-y-4">
      <WelcomeCard userData={userData} />
      <RequestSummary requests={requests} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RecentDocuments documents={documents} />
        <RecentInvoices invoices={invoices} />
      </div>
      <HelpCard />
    </div>
  );
}

function WelcomeCard({
  userData,
}: {
  userData: { name: string; plan: string; address: string };
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Bienvenue, {userData.name}</CardTitle>
        <CardDescription>
          Voici un aperçu de votre abonnement et de vos services
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">
              Votre offre
            </p>
            <p className="font-medium">{userData.plan}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">
              Adresse d&apos;installation
            </p>
            <p className="font-medium">{userData.address}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RequestSummary({ requests }: { requests: DashboardRequest[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Suivi de mes demandes</CardTitle>
        <CardDescription>
          État actuel de vos demandes de raccordement
        </CardDescription>
      </CardHeader>
      <CardContent>
        {requests.length > 0 ? (
          <div className="space-y-6">
            {requests.map((request) => (
              <div key={request.id} className="space-y-2">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">{request.address}</p>
                    <div className="flex items-center mt-1">
                      <Badge
                        variant="outline"
                        className={getStatusColor(request.status)}
                      >
                        {request.status}
                      </Badge>
                      <span className="ml-2 text-sm text-gray-500">
                        Demande du {request.date}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{request.progress}%</p>
                    <p className="text-sm text-gray-500">complété</p>
                  </div>
                </div>
                <Progress value={request.progress} className="h-2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500">Aucune demande en cours</p>
            <Link to="/offres">
              <Button variant="outline" className="mt-4">
                Découvrir nos offres
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentDocuments({ documents }: { documents: DashboardDocument[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Documents récents</CardTitle>
      </CardHeader>
      <CardContent>
        {documents.slice(0, 2).map((doc) => (
          <div
            key={doc.id}
            className="flex justify-between items-center py-2"
          >
            <div>
              <p className="font-medium">{doc.name}</p>
              <p className="text-sm text-gray-500">{doc.date}</p>
            </div>
            <Badge variant="outline" className={getStatusColor(doc.status)}>
              {doc.status}
            </Badge>
          </div>
        ))}
        {documents.length === 0 && (
          <p className="text-sm text-gray-500">Aucun document récent.</p>
        )}
        <Link to="/dashboard/documents">
          <Button variant="ghost" className="w-full mt-4">
            Voir tous les documents
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function RecentInvoices({ invoices }: { invoices: DashboardInvoice[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dernières factures</CardTitle>
      </CardHeader>
      <CardContent>
        {invoices.map((invoice) => (
          <div
            key={invoice.id}
            className="flex justify-between items-center py-2"
          >
            <div>
              <p className="font-medium">{invoice.amount}</p>
              <p className="text-sm text-gray-500">{invoice.date}</p>
            </div>
            <Badge variant="outline" className={getStatusColor(invoice.status)}>
              {invoice.status}
            </Badge>
          </div>
        ))}
        {invoices.length === 0 && (
          <p className="text-sm text-gray-500">Aucune facture pour le moment.</p>
        )}
        <Link to="/dashboard/factures">
          <Button variant="ghost" className="w-full mt-4">
            Voir toutes les factures
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function HelpCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Besoin d&apos;aide ?</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col sm:flex-row gap-4">
        <Button variant="outline" className="flex-1" asChild>
          <Link to="/dashboard/assistance">
            <HelpCircle className="mr-2 h-4 w-4" />
            Centre d&apos;aide
          </Link>
        </Button>
        <Button variant="outline" className="flex-1" asChild>
          <Link to="/dashboard/messages">
            <MessageSquare className="mr-2 h-4 w-4" />
            Contacter un conseiller
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
